import React, { useEffect } from 'react';
import { Form, message } from 'antd';
import { Checkbox, Icon, Input, Spinner } from 'ui';
import { Dayjs } from 'dayjs';
import DiscountType from '../../../enums/discount-type';
import useQueryApiClient from 'utils/useQueryApiClient';
import { useCoupon } from './hooks/use-coupon';
import { useCategories } from './hooks/use-categories';
import { useProducts } from './hooks/use-product';
import { useTranslations } from './hooks/use-translations';
import { useAuth } from '../../../hooks/useAuth';
import CategorySelectField from './components/CategorySelectField';
import DiscountField from './components/DiscountField';
import ProductSelectField from './components/ProductSelectField';
import PeriodField from './components/PeriodField';
import CouponDrawerFooter from './components/CouponDrawerFooter';
import { StyledCouponCreateUpdateDrawer, StyledForm } from './style';
import { useBreakpoint } from 'utils/useBreakpoint';
import { useIntl } from 'react-intl';

export interface CouponFormValues {
  name: string;
  code: string;
  discount_value: number;
  discount_type: DiscountType;
  apply_to_whole_cart: boolean;
  category_ids: number[];
  product_ids: number[];
  period: [Dayjs, Dayjs];
  active: boolean;
  for_emarket: boolean;
  for_wholesale: boolean;
  limit: number;
}

interface Props {
  id?: number;
  isVisible: boolean;
  onClose: Function;
}

const CouponCreateUpdateDrawer = ({ id, isVisible, onClose }: Props) => {
  const t = useTranslations();
  const [form] = Form.useForm<CouponFormValues>();
  const applyToWholeCart = Form.useWatch<boolean>('apply_to_whole_cart', form);
  const { user } = useAuth();
  const farmId = user?.farmId;
  const { isLgDown } = useBreakpoint();
  const intl = useIntl();

  const couponQuery = useCoupon(id, (data) => {
    form.setFieldsValue({
      ...data,
      period: [data.start_date, data.end_date],
    });
  });
  const categoriesQuery = useCategories();
  const productsQuery = useProducts();

  useEffect(() => {
    if (!id) form.resetFields();
  }, [id, form]);

  const innerOnClose = () => {
    form.resetFields();
    onClose();
  };

  const { appendData: SubmitCoupon } = useQueryApiClient({
    request: {
      url: id
        ? `api/v2/farms/${farmId}/coupons/${id}`
        : `api/v2/farms/${farmId}/coupons`,
      method: id ? 'PATCH' : 'POST',
    },
    onSuccess: () => onClose(),
  });

  const clearCategoryAndProduct = (value: boolean) => {
    if (value) {
      form.setFieldValue('category_ids', []);
      form.setFieldValue('product_ids', []);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const [startDay, endDay] = values.period || [undefined, undefined];
      const toUtcMidnight = (d: Dayjs) => {
        return d?.isValid?.()
          ? d.format('YYYY-MM-DD') + 'T00:00:00.000Z'
          : null;
      };

      const start_date = toUtcMidnight(startDay);
      const end_date = toUtcMidnight(endDay);
      const active = values.active === true;
      const discount_value = Number(values.discount_value);
      SubmitCoupon({ ...values, discount_value, start_date, end_date, active });
    } catch {
      message.error(t('message.error'));
    }
  };

  return (
    <StyledCouponCreateUpdateDrawer
      width={isLgDown ? '100%' : '50%'}
      open={isVisible}
      onClose={innerOnClose}
      title={t(id ? 'general.edit' : 'coupons.create')}
      zIndex={1100}
      footer={
        <CouponDrawerFooter
          id={id}
          onClose={innerOnClose}
          handleSubmit={handleSubmit}
        />
      }
      closeIcon={<Icon name={'global-close-icon'} />}
      destroyOnClose
    >
      <StyledForm
        form={form}
        initialValues={{ active: true }}
        layout={'vertical'}
      >
        <Spinner spinning={couponQuery.isLoading}>
          <Form form={form} layout={'vertical'}>
            <Checkbox name={'active'} label={t('coupons.table.status')} />
            <p>{intl.formatMessage({ id: 'coupons.form.apply_to' })}</p>
            <Checkbox
              name={'for_emarket'}
              label={t('coupons.table.emarket')}
              validations={['atLeastOneApplyTo']}
              dependencies={['for_wholesale']}
            />
            <Checkbox
              name={'for_wholesale'}
              label={t('coupons.table.business')}
              validations={['atLeastOneApplyTo']}
              dependencies={['for_emarket']}
            />
            <Input
              name="name"
              label={t('coupons.table.name')}
              validations={'required'}
            />
            <Input
              name="code"
              label={t('coupons.table.coupon')}
              validations={'required'}
            />
            <Input
              name="limit"
              type="number"
              label={t('coupons.table.limit')}
            />
            <DiscountField />
            <Checkbox
              name={'apply_to_whole_cart'}
              label={t('coupons.table.apply_to_whole_cart')}
              onChange={clearCategoryAndProduct}
            />
            <CategorySelectField
              categories={categoriesQuery.data}
              disabled={applyToWholeCart}
            />
            <ProductSelectField
              products={productsQuery.data}
              disabled={applyToWholeCart}
            />
            <PeriodField />
          </Form>
        </Spinner>
      </StyledForm>
    </StyledCouponCreateUpdateDrawer>
  );
};

export default CouponCreateUpdateDrawer;
